import apiService from '../services/api';
import { getCustomQuickResponsesFromMetadata } from './quickResponses';
import * as tokenStorage from './tokenStorage';

let cachedLabels: string[] | null = null;
let inflight: Promise<string[]> | null = null;

export function readStoredCustomQuickResponses(): string[] {
  try {
    const raw = tokenStorage.getUser();
    if (!raw) return [];
    return getCustomQuickResponsesFromMetadata(JSON.parse(raw)?.user_metadata);
  } catch {
    return [];
  }
}

export function getCachedCustomQuickResponses(): string[] {
  return cachedLabels ?? readStoredCustomQuickResponses();
}

export function invalidateCustomQuickResponses(): void {
  cachedLabels = null;
  inflight = null;
}

function persistUserMetadata(user: {
  id?: string;
  email?: string;
  name?: string;
  full_name?: string;
  user_metadata?: unknown;
}): void {
  try {
    const existingRaw = tokenStorage.getUser();
    const existing = existingRaw ? JSON.parse(existingRaw) : {};
    tokenStorage.setUser(
      JSON.stringify({
        ...existing,
        id: user.id ?? existing.id,
        email: user.email ?? existing.email,
        name: user.name ?? existing.name,
        full_name: user.full_name ?? existing.full_name,
        user_metadata: user.user_metadata ?? existing.user_metadata,
      }),
    );
  } catch {
    // Ignore storage failures; in-memory cache is enough for this session.
  }
}

/** Fetch the signed-in user's custom Quick Responses from the backend. */
export async function loadCustomQuickResponses(force = false): Promise<string[]> {
  if (!force && cachedLabels) return cachedLabels;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.success && response.data) {
        const labels = getCustomQuickResponsesFromMetadata(response.data.user_metadata);
        cachedLabels = labels;
        persistUserMetadata(response.data);
        return labels;
      }
    } catch {
      // Fall back to whatever is already stored locally.
    } finally {
      inflight = null;
    }
    const fallback = readStoredCustomQuickResponses();
    cachedLabels = fallback;
    return fallback;
  })();

  return inflight;
}
